/**
 * UniversalDockButton
 * 
 * Reusable floating dock button component for operations panels across the app.
 * Always visible, fixed to viewport top-right, with consistent styling.
 */

'use client';

import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UniversalDockButtonProps {
  onClick: () => void;
  isOpen?: boolean;
  label?: string;
  icon?: React.ReactNode;
  className?: string;
  tooltip?: string;
}

/**
 * Universal Dock Button
 * 
 * Fixed position button that stays in top-right corner while scrolling.
 * Minimum 44x44px for accessibility, typically 52x52px.
 * Exposes ref for positioning panels adjacent to it.
 */
export const UniversalDockButton = React.forwardRef<HTMLButtonElement, UniversalDockButtonProps>(
  function UniversalDockButton({
    onClick,
    isOpen = false,
    label = 'Operations',
    icon,
    className,
    tooltip = 'Operations',
  }, ref) {
    const defaultIcon = icon || <SlidersHorizontal className="h-5 w-5" />;

    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={cn(
          // Base styles - fixed top-right, below nav
          "fixed top-[88px] right-4 z-[100]",
          // Size - minimum 44x44, prefer 52x52
          "h-[52px] w-[52px] min-h-[44px] min-w-[44px]",
          // Pill shape with rounded corners
          "rounded-2xl",
          // Background and border
          "border border-white/10 bg-slate-950/90 backdrop-blur-md",
          "shadow-xl",
          // Hover and focus states
          "hover:bg-slate-900/95 hover:border-white/20",
          "focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-slate-950",
          // Transitions
          "transition-all duration-200",
          // Flexbox for icon centering
          "flex items-center justify-center",
          // Cursor
          "cursor-pointer",
          // Open state styling
          isOpen && "bg-slate-900/95 border-white/20",
          className
        )}
        aria-label={tooltip}
        title={tooltip}
      >
        <div
          className={cn(
            "text-slate-200 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        >
          {defaultIcon}
        </div>
      </button>
    );
  }
);
